import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Scale, ShoppingCart, CreditCard, Truck, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageSeo } from '@/components/seo/PageSeo';
import { useNavigate } from 'react-router-dom';

const TermsOfServicePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  const content = {
    de: {
      title: "NUTZUNGSBEDINGUNGEN",
      effectiveDate: "Gültig ab: 10 Januar 2026",
      intro: {
        title: "Einführung",
        text: `Willkommen bei Turkish E-Market (turkishemarket.com). Diese Nutzungsbedingungen regeln Ihre Nutzung unserer Website, mobilen Anwendung und aller damit verbundenen Dienste. Durch die Nutzung unserer Plattform erklären Sie sich mit diesen Bedingungen einverstanden.`
      },
      section1: {
        title: "1. Definitionen",
        items: [
          '"Plattform" bezeichnet die Website turkishemarket.com und alle zugehörigen mobilen Anwendungen.',
          '"Verkäufer" bezeichnet Drittanbieter, die Produkte über unsere Plattform anbieten.',
          '"Käufer" bezeichnet jeden Benutzer, der Produkte über unsere Plattform kauft.',
          '"E-Market Germany UG" ist der Betreiber der Plattform.'
        ]
      },
      section2: {
        title: "2. Kontoinformationen",
        text: `Um bestimmte Funktionen unserer Plattform nutzen zu können, müssen Sie möglicherweise ein Konto erstellen. Sie sind verantwortlich für die Wahrung der Vertraulichkeit Ihrer Kontodaten und für alle Aktivitäten, die unter Ihrem Konto stattfinden.`
      },
      section3: {
        title: "3. Bestellungen und Zahlungen",
        text: `Alle Bestellungen unterliegen der Verfügbarkeit und Preisbestätigung. Wir behalten uns das Recht vor, Bestellungen abzulehnen. Zahlungen werden sicher über unsere Zahlungspartner abgewickelt.`
      },
      section4: {
        title: "4. Versand und Lieferung",
        text: `Lieferzeiten und -kosten variieren je nach Standort und Verkäufer. Details finden Sie auf der jeweiligen Produktseite. Wir sind nicht verantwortlich für Verzögerungen durch Zoll oder andere externe Faktoren.`
      },
      section5: {
        title: "5. Rückgaben und Erstattungen",
        text: `Rückgaberichtlinien variieren je nach Verkäufer. Bitte überprüfen Sie die Rückgabebedingungen auf der Produktseite. Bei Problemen kontaktieren Sie bitte unseren Kundenservice.`
      },
      section6: {
        title: "6. Haftungsbeschränkung",
        text: `E-Market Germany UG haftet nicht für indirekte, zufällige oder Folgeschäden, die sich aus der Nutzung unserer Plattform ergeben. Unsere Haftung ist auf den Kaufpreis des betreffenden Produkts beschränkt.`
      },
      section7: {
        title: "7. Änderungen der Bedingungen",
        text: `Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern. Wesentliche Änderungen werden auf unserer Plattform bekannt gegeben.`
      },
      contact: {
        title: "Kontakt",
        text: `Bei Fragen zu diesen Nutzungsbedingungen kontaktieren Sie uns bitte unter:
E-Market Germany UG
Thessaloniki Allee 12, 51103 Köln, Deutschland
E-Mail: info@turkishemarket.com`
      }
    },
    en: {
      title: "TERMS OF SERVICE",
      effectiveDate: "Effective Date: January 10, 2026",
      intro: {
        title: "Introduction",
        text: `Welcome to Turkish E-Market (turkishemarket.com). These Terms of Service govern your use of our website, mobile application, and all related services. By using our platform, you agree to these terms.`
      },
      section1: {
        title: "1. Definitions",
        items: [
          '"Platform" refers to the website turkishemarket.com and all related mobile applications.',
          '"Seller" refers to third-party vendors offering products through our platform.',
          '"Buyer" refers to any user purchasing products through our platform.',
          '"E-Market Germany UG" is the operator of the platform.'
        ]
      },
      section2: {
        title: "2. Account Information",
        text: `To use certain features of our platform, you may need to create an account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.`
      },
      section3: {
        title: "3. Orders and Payments",
        text: `All orders are subject to availability and price confirmation. We reserve the right to refuse orders. Payments are securely processed through our payment partners.`
      },
      section4: {
        title: "4. Shipping and Delivery",
        text: `Delivery times and costs vary by location and seller. Details can be found on the respective product page. We are not responsible for delays caused by customs or other external factors.`
      },
      section5: {
        title: "5. Returns and Refunds",
        text: `Return policies vary by seller. Please check the return conditions on the product page. For issues, please contact our customer service.`
      },
      section6: {
        title: "6. Limitation of Liability",
        text: `E-Market Germany UG is not liable for indirect, incidental, or consequential damages arising from the use of our platform. Our liability is limited to the purchase price of the relevant product.`
      },
      section7: {
        title: "7. Changes to Terms",
        text: `We reserve the right to change these terms at any time. Significant changes will be announced on our platform.`
      },
      contact: {
        title: "Contact",
        text: `For questions about these Terms of Service, please contact us at:
E-Market Germany UG
Thessaloniki Allee 12, 51103 Cologne, Germany
Email: info@turkishemarket.com`
      }
    },
    tr: {
      title: "KULLANIM KOŞULLARI",
      effectiveDate: "Yürürlük Tarihi: 10 Ocak 2026",
      intro: {
        title: "Giriş",
        text: `Turkish E-Market'e (turkishemarket.com) hoş geldiniz. Bu Kullanım Koşulları, web sitemizi, mobil uygulamamızı ve tüm ilgili hizmetlerimizi kullanımınızı düzenler. Platformumuzu kullanarak bu koşulları kabul etmiş olursunuz.`
      },
      section1: {
        title: "1. Tanımlar",
        items: [
          '"Platform", turkishemarket.com web sitesi ve tüm ilgili mobil uygulamaları ifade eder.',
          '"Satıcı", platformumuz üzerinden ürün sunan üçüncü taraf satıcıları ifade eder.',
          '"Alıcı", platformumuz üzerinden ürün satın alan herhangi bir kullanıcıyı ifade eder.',
          '"E-Market Germany UG", platformun işletmecisidir.'
        ]
      },
      section2: {
        title: "2. Hesap Bilgileri",
        text: `Platformumuzun belirli özelliklerini kullanmak için bir hesap oluşturmanız gerekebilir. Hesap bilgilerinizin gizliliğini korumak ve hesabınız altında gerçekleşen tüm faaliyetlerden siz sorumlusunuz.`
      },
      section3: {
        title: "3. Siparişler ve Ödemeler",
        text: `Tüm siparişler stok durumu ve fiyat onayına tabidir. Siparişleri reddetme hakkımızı saklı tutarız. Ödemeler, ödeme ortaklarımız aracılığıyla güvenli bir şekilde işlenir.`
      },
      section4: {
        title: "4. Kargo ve Teslimat",
        text: `Teslimat süreleri ve maliyetleri konuma ve satıcıya göre değişir. Ayrıntılar ilgili ürün sayfasında bulunabilir. Gümrük veya diğer dış faktörlerden kaynaklanan gecikmelerden sorumlu değiliz.`
      },
      section5: {
        title: "5. İade ve Geri Ödemeler",
        text: `İade politikaları satıcıya göre değişir. Lütfen ürün sayfasındaki iade koşullarını kontrol edin. Sorunlar için lütfen müşteri hizmetlerimizle iletişime geçin.`
      },
      section6: {
        title: "6. Sorumluluk Sınırlaması",
        text: `E-Market Germany UG, platformumuzun kullanımından kaynaklanan dolaylı, arızi veya sonuç olarak ortaya çıkan zararlardan sorumlu değildir. Sorumluluğumuz ilgili ürünün satın alma fiyatı ile sınırlıdır.`
      },
      section7: {
        title: "7. Koşulların Değiştirilmesi",
        text: `Bu koşulları herhangi bir zamanda değiştirme hakkımızı saklı tutarız. Önemli değişiklikler platformumuzda duyurulacaktır.`
      },
      contact: {
        title: "İletişim",
        text: `Bu Kullanım Koşulları hakkında sorularınız için lütfen bizimle iletişime geçin:
E-Market Germany UG
Thessaloniki Allee 12, 51103 Köln, Almanya
E-posta: info@turkishemarket.com`
      }
    }
  };

  const c = content[currentLang] || content['en'];

  return (
    <div className="min-h-screen bg-background" data-testid="terms-of-service-page">
      <PageSeo title={c.title} description={c.intro?.text} segments={['terms']} />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>

        <div className="bg-card rounded-lg shadow-sm border p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-full">
              <Scale className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{c.title}</h1>
              <p className="text-muted-foreground text-sm">{c.effectiveDate}</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.intro.title}</h2>
              </div>
              <p className="text-muted-foreground whitespace-pre-line">{c.intro.text}</p>
            </section>

            {/* Section 1 - Definitions */}
            <section>
              <h2 className="text-xl font-semibold">{c.section1.title}</h2>
              <ul className="list-disc pl-6 space-y-2">
                {c.section1.items.map((item, idx) => (
                  <li key={idx} className="text-muted-foreground">{item}</li>
                ))}
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-semibold">{c.section2.title}</h2>
              <p className="text-muted-foreground">{c.section2.text}</p>
            </section>

            {/* Section 3 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.section3.title}</h2>
              </div>
              <p className="text-muted-foreground">{c.section3.text}</p>
            </section>

            {/* Section 4 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.section4.title}</h2>
              </div>
              <p className="text-muted-foreground">{c.section4.text}</p>
            </section>

            {/* Section 5 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <RefreshCw className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.section5.title}</h2>
              </div>
              <p className="text-muted-foreground">{c.section5.text}</p>
            </section>

            {/* Section 6 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.section6.title}</h2>
              </div>
              <p className="text-muted-foreground">{c.section6.text}</p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-semibold">{c.section7.title}</h2>
              <p className="text-muted-foreground">{c.section7.text}</p>
            </section>

            {/* Contact */}
            <section className="bg-muted/50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-3">{c.contact.title}</h2>
              <p className="text-muted-foreground whitespace-pre-line">{c.contact.text}</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
